/** Detail-view listing reads — ONE definition, two callers.
 *
 *  A detail page needs a body (`recipe` / `manifest`), which no list read
 *  carries, plus the bounded extras that decide the install CTA: the pack that
 *  owns a bundled recipe's install, and the display listings for its pinned
 *  members. The SSR worker fetched all of that; the hydrated SPA got it for free
 *  by holding the whole catalogue in memory.
 *
 *  It no longer does — the SPA pages from `/catalog/search` and holds one page —
 *  so it has to fetch the same things. Reimplementing them SPA-side would fork
 *  the resolution that decides whether a pack can own an install: a
 *  trust-relevant, fail-closed walk whose two copies would drift silently, one
 *  offering an install the other refuses. So it lives here instead, with the
 *  transport injected, and both callers run the same code.
 *
 *  Everything here is fail-closed by construction. A malformed response, an
 *  ambiguous identity, or a fetch failure yields `undefined` / `[]`, which makes
 *  the template render a DISABLED workflow install rather than falling back to a
 *  single-recipe install the pack was supposed to own.
 */

import {
  BULK_PACK_MAX_RECIPES,
  extractBulkPackRecipeRefs,
  parseRecipeBundleKey,
  SLUG_RE,
  validateRecipeBundleKey,
  type RecipeBundleCatalogRecipe,
} from '@recued/contracts';
import type {
  PackListing,
  RecipeBundleInstallContext,
  RecipeListing,
} from './template.js';

/** One PostgREST read. `path` is everything after `/rest/v1/` — the caller owns
 *  the project URL and the anon-key headers, which is the only thing that
 *  differs between a Worker and the browser. */
export type ListingRead = (path: string) => Promise<Response>;

/** Detail selects — these DO carry the body, unlike `LISTING_SELECT`. One row at
 *  a time is the only place that is affordable: a pack manifest is median 53 KB
 *  and p90 258 KB. */
export const RECIPE_LISTING_SELECT =
  'recipe_id,publisher_id,name,description,version,platforms,tags,download_count,view_count,rating_avg,rating_count,recipe,updated_at,created_at';
export const PACK_LISTING_SELECT =
  'slug,publisher_id,name,description,version,pack_kind,manifest,trust_summary,tags,download_count,view_count,updated_at,created_at';

const firstRow = async (read: ListingRead, path: string): Promise<Record<string, unknown> | null> => {
  const res = await read(path);
  if (!res.ok) throw new Error(`Failed to load details: ${res.status}`);
  const rows = (await res.json()) as unknown;
  return Array.isArray(rows) && rows.length > 0 ? (rows[0] as Record<string, unknown>) : null;
};

/** `&order=publisher_id.asc` on a `limit=1` slug lookup.
 *
 *  The tables are keyed `(recipe_id, publisher_id)` / `(slug, publisher_id)` but
 *  every public surface — this detail read, the SSR head pointer, the CDN cache
 *  key, the install-manifest JSON — addresses a listing by SLUG ALONE. Two
 *  publishers holding one slug therefore made `limit=1` a coin flip with no
 *  ORDER BY: Postgres may return either row, and whichever won the race got
 *  baked into the KV/CDN entry that everyone else then fetched.
 *
 *  Migration 031 closes this properly with a global unique index on the slug, so
 *  the ambiguity cannot exist once it is applied. This ordering is what holds
 *  until then, and it stays afterwards because a `limit` without an `order` is
 *  an unspecified result either way — the cost is nothing and it removes a class
 *  of "the page changed and nobody deployed anything". Ordering is arbitrary but
 *  STABLE, which is the whole point; it deliberately does not try to pick a
 *  "right" publisher, because there isn't one. */
const DETERMINISTIC_FIRST = '&order=publisher_id.asc&limit=1';

/** ONE recipe listing, body included. `null` ⇒ the row is not published (it was
 *  unpublished between the catalogue read and now), which is a real answer and
 *  not an error. */
export const fetchRecipeDetail = async (
  read: ListingRead,
  slug: string,
): Promise<RecipeListing | null> =>
  (await firstRow(
    read,
    `recipes?recipe_id=eq.${encodeURIComponent(slug)}&status=eq.published`
    + `&select=${RECIPE_LISTING_SELECT}${DETERMINISTIC_FIRST}`,
  )) as RecipeListing | null;

/** ONE pack listing, manifest + trust summary included. */
export const fetchPackDetail = async (
  read: ListingRead,
  slug: string,
): Promise<PackListing | null> =>
  (await firstRow(
    read,
    `packs?slug=eq.${encodeURIComponent(slug)}&status=eq.published`
    + `&select=${PACK_LISTING_SELECT}${DETERMINISTIC_FIRST}`,
  )) as PackListing | null;

/** Display-only recipe enrichment for a pack. The manifest refs remain the
 *  source of truth; this query is capped at the BulkPack recipe limit and any
 *  duplicate marketplace identity is dropped instead of chosen arbitrarily. */
export const fetchRecipeListingsByRefs = async (
  read: ListingRead,
  refs: ReadonlyArray<{ slug: string; version: number }>,
): Promise<RecipeListing[]> => {
  const uniqueSlugs = [...new Set(refs.map((ref) => ref.slug))];
  if (
    uniqueSlugs.length === 0
    || uniqueSlugs.length > BULK_PACK_MAX_RECIPES
    || uniqueSlugs.some((slug) => !SLUG_RE.test(slug))
  ) return [];
  const requestedVersion = new Map(refs.map((ref) => [ref.slug, ref.version]));
  const inList = encodeURIComponent(uniqueSlugs.map((slug) => `"${slug}"`).join(','));
  try {
    const res = await read(
      `recipes?status=eq.published`
      + `&recipe_id=in.(${inList})&limit=${BULK_PACK_MAX_RECIPES + 1}`
      + `&select=${RECIPE_LISTING_SELECT}`,
    );
    if (!res.ok) return [];
    const json = (await res.json()) as unknown;
    if (!Array.isArray(json) || json.length > BULK_PACK_MAX_RECIPES) return [];
    const candidates = json.filter((raw): raw is RecipeListing => {
      if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return false;
      const row = raw as Record<string, unknown>;
      return typeof row.recipe_id === 'string'
        && typeof row.publisher_id === 'string'
        && typeof row.version === 'number'
        && requestedVersion.get(row.recipe_id) === row.version;
    });
    const counts = new Map<string, number>();
    for (const listing of candidates) {
      counts.set(listing.recipe_id, (counts.get(listing.recipe_id) ?? 0) + 1);
    }
    return candidates.filter((listing) => counts.get(listing.recipe_id) === 1);
  } catch {
    return [];
  }
};

/** Cold recipe-detail lookup for the direct bundle identity. This stays
 *  request-local and bounded by the pack recipe cap: at most two same-slug pack
 *  rows (the second detects transport ambiguity), 51 declaring recipe rows
 *  (the extra row detects an unrepresentable group), and at most one bounded
 *  metadata read for pack-only extras.
 *  Any malformed response or fetch failure omits the context, which makes the
 *  template render a disabled workflow install rather than a single-recipe
 *  fallback. */
export const fetchRecipeBundleInstallContext = async (
  read: ListingRead,
  target: RecipeListing,
): Promise<RecipeBundleInstallContext | undefined> => {
  const bundleKey = target.recipe?.metadata?.recipe_bundle;
  if (
    typeof bundleKey !== 'string'
    || validateRecipeBundleKey(bundleKey).length > 0
  ) {
    return undefined;
  }
  const parsed = parseRecipeBundleKey(bundleKey);
  if (parsed === null || parsed.publisher !== target.publisher_id) return undefined;

  try {
    const [packResponse, memberResponse] = await Promise.all([
      read(
        `packs?slug=eq.${encodeURIComponent(parsed.bundle_slug)}`
        + '&status=eq.published&limit=2'
        + `&select=${PACK_LISTING_SELECT}`,
      ),
      read(
        `recipes?status=eq.published`
        + `&publisher_id=eq.${encodeURIComponent(parsed.publisher)}`
        + `&recipe->metadata->>recipe_bundle=eq.${encodeURIComponent(bundleKey)}`
        + `&limit=${BULK_PACK_MAX_RECIPES + 1}`
        + `&select=${RECIPE_LISTING_SELECT}`,
      ),
    ]);
    if (!packResponse.ok || !memberResponse.ok) return undefined;
    const rawPacks = (await packResponse.json()) as unknown;
    if (!Array.isArray(rawPacks) || rawPacks.length !== 1) return undefined;
    const rawMembers = (await memberResponse.json()) as unknown;
    if (
      !Array.isArray(rawMembers)
      || rawMembers.length === 0
      || rawMembers.length > BULK_PACK_MAX_RECIPES
    ) {
      return undefined;
    }

    const recipes: RecipeBundleCatalogRecipe[] = [];
    const declaringListings: RecipeListing[] = [];
    for (const raw of rawMembers) {
      if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
      const row = raw as Record<string, unknown>;
      if (
        typeof row.recipe_id !== 'string'
        || row.recipe_id.length === 0
        || row.publisher_id !== parsed.publisher
        || typeof row.version !== 'number'
        || !Number.isInteger(row.version)
        || row.version <= 0
        || (row.recipe as RecipeListing['recipe'] | undefined)?.metadata?.recipe_bundle !== bundleKey
      ) {
        return undefined;
      }
      declaringListings.push(row as unknown as RecipeListing);
      recipes.push({
        recipe_id: row.recipe_id,
        publisher_id: parsed.publisher,
        version: row.version,
        recipe_bundle: bundleKey,
      });
    }

    const pack = rawPacks[0] as PackListing;
    if (
      pack.slug !== parsed.bundle_slug
      || pack.publisher_id !== parsed.publisher
      || pack.manifest?.slug !== pack.slug
      || pack.manifest?.publisher !== pack.publisher_id
    ) {
      return undefined;
    }
    const recipe_refs = extractBulkPackRecipeRefs(pack.manifest);
    if (recipe_refs === null) return undefined;
    const declaringSlugs = new Set(declaringListings.map((listing) => listing.recipe_id));
    const extraRefs = recipe_refs.filter((ref) => !declaringSlugs.has(ref.slug));
    const extraListings = extraRefs.length > 0
      ? await fetchRecipeListingsByRefs(read, extraRefs)
      : [];
    return {
      recipes,
      packs: [{
        slug: pack.slug,
        publisher_id: pack.publisher_id,
        recipe_refs,
        listing: pack,
      }],
      recipeListings: [...declaringListings, ...extraListings],
    };
  } catch {
    return undefined;
  }
};

/** The pack-detail companion: the display listings for a recipe-only pack's
 *  pinned members. `recipeOnly` is the same gate the SSR applied — a pack that
 *  also installs ingredients renders the generic contents list instead. */
export const fetchPackRecipeListings = async (
  read: ListingRead,
  pack: PackListing,
): Promise<RecipeListing[] | undefined> => {
  const refs = extractBulkPackRecipeRefs(pack.manifest);
  const contents = pack.manifest?.contents;
  const recipeOnly = refs !== null
    && refs.length > 0
    && (contents === undefined
      || (Array.isArray(contents) && contents.every((entry) => entry?.type === 'recipe')));
  if (!recipeOnly || refs === null) return undefined;
  return fetchRecipeListingsByRefs(read, refs);
};
